import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import type { Task } from "../types";

export interface ListTasksParams {
  status?: string;
  task_type?: string;
  contact_id?: string;
  campaign_id?: string;
  assigned_to?: string;
  /** Task taxonomy filters — `human` matches rows without the field too. */
  executor?: "human" | "agent";
  /** Proposer filter: an agent uuid, `any` (agent-proposed), or `none`. */
  from_agent?: string;
  assigned_agent?: string;
  /** `open` / `closed` – several statuses at once; closed = newest close first. */
  state?: "open" | "closed";
  /** Leave one task type out (the History view drops `attention`). */
  exclude_type?: string;
  limit?: number;
  offset?: number;
  [key: string]: unknown;
}

/**
 * One task the owner carries out, in the queue shape – mirrors
 * `system/services/task_outcomes.py :: serialize_human_task` (the same item
 * the Copilot extension renders). Change them together.
 */
export interface HumanTask {
  task_id: string;
  title: string | null;
  /** The ≤2-sentence why. */
  description: string | null;
  /** The longer "more context". */
  details: string | null;
  task_type: string | null;
  status: string;
  audience: "people" | "team";
  agent_slug: string | null;
  agent_id: string | null;
  contact_id: string | null;
  contact_name: string | null;
  company_name: string | null;
  linkedin_url: string | null;
  message_url: string | null;
  action_url: string | null;
  draft_message: string | null;
  priority: "low" | "normal" | "high" | string | null;
  scheduled_date: string | null;
  expires_at: string | null;
  batch: { key: string; label?: string | null } | null;
  /** The campaign bundle the task belongs to (`review_bundles.id`). */
  bundle_id: string | null;
  campaign_slug: string | null;
  closure: Record<string, unknown> | null;
  /** Set when an employee's execution failed and the task came back to the owner. */
  agent_error: string | null;
  /** 1 until the seat revises the task in place (`task_revise`). */
  version: number;
  /** Set while the owner sent the task back with a note (and after an
   * unanswered round: `released: "unrevised"`). */
  revision: TaskRevisionMarker | null;
  created_at: string | null;
  updated_at: string | null;
}

/** `serialize_human_task → revision` – mirrors `task_revisions.revision_projection`. */
export interface TaskRevisionMarker {
  round_id: string;
  note: string;
  since: string | null;
  seat: string | null;
  released: "unrevised" | null;
}

/** One revision of a task – the values before it, the owner's note, the seat's note. */
export interface TaskRevision {
  version: number;
  at: string;
  by: string | null;
  round_id: string | null;
  requested_at: string | null;
  owner_note: string | null;
  seat_note: string | null;
  fields: Record<string, string | null | Record<string, unknown>>;
}

/** One event of a task's history – mirrors `services/task_detail.py :: task_history`. */
export interface TaskEvent {
  at: string | null;
  kind:
    | "created"
    | "delegated"
    | "completed"
    | "declined"
    | "expired"
    | "reopened"
    | "withdrawn"
    | "sent_back"
    | "revised"
    | "not_revised";
  actor: string | null;
  text: string;
  note: string | null;
}

/** A queue job that carries the task's id (admin callers only). */
export interface TaskJob {
  id: string;
  job_type: string;
  status: string;
  scope_type: string;
  scope_id: string;
  created_at: string | null;
  ended_at: string | null;
  error: string | null;
}

/** `GET /api/v1/tasks/{id}` → `detail` – mirrors `task_detail.build_task_detail`. */
export interface TaskDetail {
  item: HumanTask;
  executor: "human" | "agent";
  assigned_agent_slug: string | null;
  assigned_agent_id: string | null;
  agent_error: string | null;
  closure: Record<string, unknown> | null;
  withdrawal: { reason?: string; at?: string } | null;
  revisions: TaskRevision[];
  contact: { id: string; name: string | null; company: string | null } | null;
  campaign: { slug: string; name: string } | null;
  bundle: { id: string; title: string | null } | null;
  history: TaskEvent[];
  /** Null when the caller is not an admin – no job links then. */
  jobs: TaskJob[] | null;
}

/** Progress of one batch across all its tasks, closed ones included. */
export interface TaskBatchProgress {
  key: string;
  label: string;
  total: number;
  open: number;
  completed: number;
  bundle_id: string | null;
  campaign_slug: string | null;
  campaign_name: string | null;
}

/** `GET /api/v1/tasks/queue` – mirrors `build_task_list_payload`. Items come
 * sorted by urgency: soonest expiry, then priority, then oldest. */
export interface TaskQueue {
  items: HumanTask[];
  count: number;
  people_count: number;
  team_count: number;
  truncated: boolean;
  batches: TaskBatchProgress[];
}

export const tasksApi = {
  list: (params: ListTasksParams = {}) =>
    api.get<Task[]>("/api/v1/tasks", params),
  queue: (campaign?: string) =>
    api.get<TaskQueue>("/api/v1/tasks/queue", campaign ? { campaign } : undefined),
  counts: (assigned_to?: string) =>
    api.get<Record<string, number>>("/api/v1/tasks/counts", { assigned_to }),
  get: (id: string) => api.get<Task & { detail: TaskDetail }>(`/api/v1/tasks/${id}`),
  setStatus: (id: string, status: string, extra?: Record<string, unknown>) =>
    api.post<Task>(`/api/v1/tasks/${id}/status`, { status, extra }),
  dismiss: (id: string, opts?: { actor?: string; reason?: string }) =>
    api.post<Task>(`/api/v1/tasks/${id}/dismiss`, opts ?? {}),
  complete: (id: string, opts?: { actor?: string; feedback?: string }) =>
    api.post<Task>(`/api/v1/tasks/${id}/complete`, opts ?? {}),
  /** Send a group (its batch key) or single tasks back to their seats with one note. */
  requestChanges: (body: { note: string; group_key?: string; task_ids?: string[] }) =>
    api.post<{ rounds: { round_id: string; seat: string; job_id: string; task_ids: string[] }[]; skipped: { task_id: string; reason: string }[] }>(
      "/api/v1/tasks/request-changes",
      body,
    ),
  /** Take a round back: the markers go, a still-queued job is canceled. */
  takeBack: (roundId: string) => api.post<{ cleared: number }>(`/api/v1/tasks/revisions/${roundId}/take-back`, {}),
  /** Undo a close (Done, Decline – not a withdrawal). */
  reopen: (id: string) => api.post<Task>(`/api/v1/tasks/${id}/reopen`, {}),
  patch: (id: string, data: Record<string, unknown>) =>
    api.patch<Task>(`/api/v1/tasks/${id}`, { data }),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/api/v1/tasks/${id}`),
};

/** Statuses under which a task still waits for the owner –
 * `task_outcomes.OPEN_HUMAN_TASK_STATUSES`. */
export const OPEN_TASK_STATUSES = ["proposed", "scheduled", "awaiting_manual"];

/** Under the `["tasks"]` family, so every task mutation's invalidation
 * repaints the queue too. */
export const TASK_QUEUE_KEY = ["tasks", "queue"] as const;

export function useTaskQueue(campaign?: string) {
  return useQuery({
    queryKey: [...TASK_QUEUE_KEY, campaign ?? null],
    queryFn: () => tasksApi.queue(campaign),
    refetchInterval: 30_000,
  });
}

/** One task in full for the drawer; off while no task is selected. */
export function useTaskDetail(id: string | null) {
  return useQuery({
    queryKey: ["tasks", "detail", id],
    queryFn: () => tasksApi.get(id as string),
    enabled: !!id,
  });
}

/** The History view: the owner's closed tasks, newest close first. */
export function useTaskHistory() {
  return useQuery({
    queryKey: ["tasks", "history"],
    queryFn: () =>
      tasksApi.list({ state: "closed", executor: "human", exclude_type: "attention", limit: 200 }),
    refetchInterval: 60_000,
  });
}

/** A raw `/api/v1/tasks` row in the queue shape (the history list's rows). */
export function humanTaskOf(task: Task): HumanTask {
  const d = task.data;
  return {
    task_id: task.id,
    title: d.subject ?? null,
    description: d.context?.reason ?? null,
    details: d.context?.details ?? null,
    task_type: d.task_type ?? null,
    status: d.status,
    audience: "people",
    agent_slug: d.proposed_by_agent_slug ?? null,
    agent_id: d.proposed_by_agent_id ?? null,
    contact_id: d.contact_id ?? null,
    contact_name: d.context?.contact_name ?? null,
    company_name: d.context?.company_name ?? null,
    linkedin_url: d.linkedin_url ?? null,
    message_url: d.message_url ?? null,
    action_url: d.action_url ?? null,
    draft_message: d.draft?.body ?? null,
    priority: d.execution?.priority ?? null,
    scheduled_date: d.scheduled_date ?? null,
    expires_at: d.expires_at ?? null,
    batch: d.batch ? { key: d.batch.key, label: d.batch.label ?? null } : null,
    bundle_id: d.bundle_id ?? null,
    campaign_slug: d.campaign_slug ?? null,
    closure: d.closure ?? null,
    agent_error: d.agent_error ?? null,
    version: typeof d.version === "number" ? d.version : 1,
    revision: null,
    created_at: task.created_at ?? null,
    updated_at: task.updated_at ?? null,
  };
}
